import SEO from "@/components/SEO";
import { PageHero, Reveal, Callout, BtnPrimary } from "@/components/Bits";
import { PRICED, CUSTOM_WORK, PRICING_NOTE, CUSTOM_WORK_NOTE } from "@/data/categories";
import { Info, PlusCircle } from "lucide-react";

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <SEO
        title="Recommended Service Charges"
        description="Middle Man recommended service charges for all 26 categories — indicative per-visit, per-hour and per-delivery price ranges in INR, with a clear explanation of what's excluded."
        path="/pricing"
      />
      <PageHero
        kicker="Middle Man · Pricing"
        title="Recommended Service Charges"
        subtitle="Fair, transparent pricing — these are the recommended service charges of a Middle Man for each category."
      />

      <div className="mx-auto max-w-5xl px-6 py-14 md:py-20">
        <Reveal>
          <div className="flex items-center gap-4 border border-[#F5D7B4] bg-brand-soft p-6" data-testid="pricing-hero-note">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-white">
              <Info className="h-5 w-5 text-brand" />
            </span>
            <p className="text-sm leading-relaxed text-ink">
              <strong className="font-semibold">Fair, transparent pricing.</strong>{" "}
              These are the recommended service charges of a Middle Man for each category.
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-10">
          <div className="overflow-hidden border border-line bg-white" data-testid="pricing-table">
            <div className="flex items-center justify-between bg-ink px-6 py-4">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-cream">Category</span>
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-cream">Service Charge</span>
            </div>
            {PRICED.map((c, i) => (
              <div
                key={c.id}
                data-testid={`pricing-row-${c.id}`}
                className={`flex items-center justify-between gap-4 border-t border-line px-6 py-4 ${i % 2 === 1 ? "bg-cream/60" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-soft">
                    <c.Icon className="h-4 w-4 text-brand" strokeWidth={1.8} />
                  </span>
                  <span className="text-sm font-semibold text-ink">{c.name}</span>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm font-semibold text-brand">
                    ₹{c.price_min.toLocaleString("en-IN")}–{c.price_max.toLocaleString("en-IN")}
                  </p>
                  <p className="text-[11px] font-medium text-mute">{c.price_unit_label}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        {CUSTOM_WORK && (
          <Reveal className="mt-8">
            <div className="border border-[#F5D090] bg-[#FDF6E7] p-7" data-testid="pricing-custom-work">
              <div className="flex items-center gap-3">
                <PlusCircle className="h-5 w-5 text-[#B45309]" />
                <h2 className="font-serif text-lg font-semibold text-[#7C2D12]">{CUSTOM_WORK.name}</h2>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-[#7C2D12]">{CUSTOM_WORK_NOTE}</p>
            </div>
          </Reveal>
        )}

        <Reveal className="mt-8">
          <div className="border border-line bg-creamalt p-8" data-testid="pricing-important-note">
            <div className="flex items-center gap-2.5">
              <Info className="h-4 w-4 text-brand" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-brand">Important Note</h2>
            </div>
            {PRICING_NOTE.split("\n\n").map((p, i) => (
              <p key={i} className="mt-4 text-sm leading-relaxed text-mute">{p}</p>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-10">
          <Callout>
            Indicative Middle Man service charges only — product/material cost, fuel, toll, parking, government fees,
            labour, loading/unloading and other third-party expenses are always extra and agreed in advance.
          </Callout>
        </Reveal>

        <Reveal className="mt-10">
          <BtnPrimary to="/services" testid="pricing-explore-services-btn">Browse all services</BtnPrimary>
        </Reveal>
      </div>
    </div>
  );
}
